import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Lock,
  CheckCircle2,
  FileSpreadsheet,
  Printer,
  Save,
  ShieldCheck,
  AlertTriangle,
  FileCheck,
  TrendingUp,
  Sparkles,
  Tag,
  HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ActionTooltip } from '@/components/common/ActionTooltip';
import { TabularNumber } from '@/components/common/TabularNumber';
import { LoadingState } from '@/components/common/LoadingState';
import { SettlementAdjustmentsGrid } from '@/components/settlements/SettlementAdjustmentsGrid';
import { SettlementAdjustmentModal } from '@/components/settlements/SettlementAdjustmentModal';
import { SettlementApprovalModal } from '@/components/settlements/SettlementApprovalModal';
import { SettlementFreezeModal } from '@/components/settlements/SettlementFreezeModal';
import { SettlementStatementPrintModal } from '@/components/settlements/SettlementStatementPrintModal';
import { SoftVoidConfirmModal } from '@/components/common/SoftVoidConfirmModal';
import { settlementService, SettlementWorkspaceData } from '@/services/settlementService';
import { SettlementAdjustmentRecord, GrowerContractRecord } from '@/types/database';
import { ContractProfitSplit, roundToCentavos } from '@/engine/settlement_calculator';
import { formatCurrency, formatDate } from '@/lib/utils';
import { deriveWorkflowStatus } from '@/lib/workflowStatus';
import { useAuth } from '@/context/AuthContext';

export const SettlementWorkspacePage: React.FC = () => {
  const { id: cycleId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAdmin, isOperations } = useAuth();

  const [workspaceData, setWorkspaceData] = useState<SettlementWorkspaceData | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<string>('');
  const [customSplit, setCustomSplit] = useState<ContractProfitSplit | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string | null>(null);

  // Modals
  const [showAdjustmentModal, setShowAdjustmentModal] = useState<boolean>(false);
  const [adjustmentToEdit, setAdjustmentToEdit] = useState<SettlementAdjustmentRecord | null>(null);
  const [showApprovalModal, setShowApprovalModal] = useState<boolean>(false);
  const [showFreezeModal, setShowFreezeModal] = useState<boolean>(false);
  const [showPrintModal, setShowPrintModal] = useState<boolean>(false);
  const [adjustmentToVoid, setAdjustmentToVoid] = useState<SettlementAdjustmentRecord | null>(null);

  const loadData = useCallback(async (overrideSplit?: ContractProfitSplit | null) => {
    if (!cycleId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await settlementService.getSettlementWorkspaceData(cycleId, overrideSplit);
      setWorkspaceData(data);
      setNotes(data.existingSettlement?.notes || '');
      if (data.cycle.contract_id) {
        setSelectedContractId(data.cycle.contract_id);
      } else if (data.availableContracts.length > 0) {
        setSelectedContractId(data.availableContracts[0].id);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settlement workspace data.');
    } finally {
      setLoading(false);
    }
  }, [cycleId]);

  useEffect(() => {
    loadData(customSplit);
  }, [loadData, customSplit]);

  // Handle ?print=1 url trigger
  useEffect(() => {
    if (searchParams.get('print') === '1' && workspaceData) {
      setShowPrintModal(true);
    }
  }, [searchParams, workspaceData]);

  const handleContractChange = (contractId: string) => {
    setSelectedContractId(contractId);
    if (!workspaceData) return;
    const contract = workspaceData.availableContracts.find((c) => c.id === contractId);
    if (contract) {
      const split: ContractProfitSplit = {
        vsb_share_pct: Number(contract.vsb_share_pct),
        grower_share_pct: Number(contract.grower_share_pct),
        source: 'grower_contract',
        contract_code: contract.contract_number || undefined,
      };
      setCustomSplit(split);
      loadData(split);
    }
  };

  const handleSaveDraft = async () => {
    if (!workspaceData || !cycleId) return;
    setSaving(true);
    setSaveSuccessMsg(null);
    setError(null);
    try {
      const res = await settlementService.saveDraftSettlement(
        cycleId,
        workspaceData.cycle.grower_id || '',
        selectedContractId || null,
        workspaceData.calculationResult,
        notes.trim() || undefined
      );

      if (!res.success) {
        throw new Error(res.error || 'Failed to save draft settlement');
      }

      setSaveSuccessMsg('Settlement draft saved successfully.');
      setTimeout(() => setSaveSuccessMsg(null), 3000);
      await loadData(customSplit);
    } catch (err: any) {
      setError(err.message || 'Failed to save draft settlement.');
    } finally {
      setSaving(false);
    }
  };

  const handleVoidAdjustment = async (reason: string) => {
    if (!adjustmentToVoid) return;
    try {
      const res = await settlementService.voidSettlementAdjustment(adjustmentToVoid.id, reason);
      if (!res.success) throw new Error(res.error || 'Failed to void adjustment');
      setAdjustmentToVoid(null);
      await loadData(customSplit);
    } catch (err: any) {
      setError(err.message || 'Failed to void adjustment.');
    }
  };

  if (loading && !workspaceData) {
    return <LoadingState message="Calculating live cycle liquidation..." />;
  }

  if (error && !workspaceData) {
    return (
      <div className="p-8 max-w-2xl mx-auto space-y-4">
        <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-3 text-rose-800 text-sm">
          <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold">Unable to load settlement workspace</h3>
            <p className="mt-1 text-xs">{error}</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/cycles')}>
          <ArrowLeft className="w-4 h-4 mr-1.5" /> Back to Cycles
        </Button>
      </div>
    );
  }

  if (!workspaceData) return null;

  const { cycle, existingSettlement, calculationResult, criticalIssues, allAdjustments } = workspaceData;
  const settlementStatus = existingSettlement?.status || 'draft';
  const isFrozen = settlementStatus === 'frozen';
  const isApproved = settlementStatus === 'approved';
  const growerName = cycle.growers?.full_name || cycle.legacy_raiser_name || 'Unassigned Grower';
  const farmLocation = `${cycle.farms?.farm_name || 'Sorosoro Ibaba'} ${cycle.production_units?.unit_code ? `/ Unit ${cycle.production_units.unit_code}` : ''}`;

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* 1. Header & Navigation Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-3 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/cycles/${cycle.id}`)}
              className="h-7 px-2 text-slate-600 hover:text-slate-900 text-xs"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Cycle Detail
            </Button>
            <span className="text-slate-300">|</span>
            <span className="text-xs font-semibold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
              Work Package 11: Settlement Engine
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-2.5">
            <h1 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight">
              Settlement Workspace: {cycle.cycle_code}
            </h1>
            <StatusBadge
              status={deriveWorkflowStatus({
                cycleStatus: cycle.status,
                settlement: existingSettlement,
              })}
              category="cycle"
            />
          </div>

          <p className="text-xs text-slate-500 mt-1">
            Grower: <span className="font-semibold text-slate-800">{growerName}</span> • Farm: <span className="font-medium text-slate-700">{farmLocation}</span> • Batch: <span className="font-mono text-slate-700">{cycle.batch_code}</span>
          </p>
        </div>

        {/* Header Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <ActionTooltip content="Generate official printable liquidation statement with complete breakdown.">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPrintModal(true)}
              className="text-xs h-9 min-h-[44px] shadow-sm text-slate-700 border-slate-300 hover:bg-slate-50"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5 text-indigo-600" /> Preview Printable PDF
            </Button>
          </ActionTooltip>

          {!isFrozen && (
            <>
              <ActionTooltip content="Save current contract split and calculation figures as an editable draft.">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveDraft}
                  disabled={saving}
                  className="text-xs h-9 min-h-[44px] border-slate-300 hover:bg-slate-50"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5 text-slate-600" /> {saving ? 'Saving...' : 'Save Draft'}
                </Button>
              </ActionTooltip>

              {isAdmin && (
                <ActionTooltip content="Permanently finalize calculated payout, lock records, and move Production Cycle to Finalized.">
                  <Button
                    size="sm"
                    onClick={() => setShowFreezeModal(true)}
                    className="text-xs h-9 min-h-[44px] bg-rose-600 hover:bg-rose-700 text-white font-bold shadow-sm"
                  >
                    <Lock className="w-3.5 h-3.5 mr-1.5" /> Finalize Settlement
                  </Button>
                </ActionTooltip>
              )}
            </>
          )}
        </div>
      </div>

      {/* Success / Error Banners */}
      {saveSuccessMsg && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-2 text-xs font-semibold text-emerald-800">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{saveSuccessMsg}</span>
        </div>
      )}

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-2 text-xs font-semibold text-rose-800">
          <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Finalized Banner */}
      {isFrozen && (
        <div className="p-4 bg-slate-900 text-white rounded-lg shadow-sm flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-rose-600 rounded-full text-white shrink-0">
              <Lock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-sm text-white">Settlement Finalized</h3>
              <p className="text-xs text-slate-300">
                Finalized on {existingSettlement?.frozen_at ? formatDate(existingSettlement.frozen_at) : 'Finalization'} by Administrator. Associated Production Cycle is Finalized.
              </p>
            </div>
          </div>
          <ActionTooltip content="Open official finalized settlement statement document.">
            <Button
              size="sm"
              onClick={() => setShowPrintModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-semibold self-start sm:self-auto"
            >
              <Printer className="w-3.5 h-3.5 mr-1.5" /> View Formal Statement
            </Button>
          </ActionTooltip>
        </div>
      )}

      {/* Lifecycle Status Stepper */}
      <div className="p-3 bg-white rounded-lg border border-slate-200 shadow-sm flex items-center justify-between text-xs">
        <div className="flex items-center gap-3">
          <span className="font-bold text-slate-700">Lifecycle:</span>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-full font-semibold border ${
              (!existingSettlement && cycle.status === 'active') ? 'bg-emerald-100 text-emerald-900 border-emerald-300' : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}>
              1. Active
            </span>
            <span className="text-slate-400">➔</span>
            <span className={`px-2.5 py-1 rounded-full font-semibold border ${
              (settlementStatus === 'draft' || settlementStatus === 'approved') ? 'bg-amber-100 text-amber-900 border-amber-300' : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}>
              2. Draft
            </span>
            <span className="text-slate-400">➔</span>
            <span className={`px-2.5 py-1 rounded-full font-semibold border ${
              (settlementStatus === 'frozen' || cycle.status === 'settled') ? 'bg-indigo-100 text-indigo-900 border-indigo-300' : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}>
              3. Finalized
            </span>
            <span className="text-slate-400">➔</span>
            <span className={`px-2.5 py-1 rounded-full font-semibold border ${
              cycle.status === 'closed' ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}>
              4. Closed
            </span>
          </div>
        </div>

        {criticalIssues.length > 0 && (
          <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded border border-amber-200 font-medium">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            <span>{criticalIssues.length} Critical Issues in Review Queue</span>
          </div>
        )}
      </div>

      {/* 2. 2-Column Ledger Summary: Credits vs Debits */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Credits (Gross Cycle Revenue) */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="p-4 bg-slate-50/80 border-b border-slate-200">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                1. Gross Cycle Revenue (Credits)
              </CardTitle>
              <span className="text-xs font-mono font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                + {formatCurrency(calculationResult.total_sales_revenue)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4 divide-y divide-slate-100 text-xs space-y-3">
            {calculationResult.settlement_lines.filter((l) => l.line_type === 'sales_revenue').map((saleLine) => (
              <div key={saleLine.line_number} className="pt-2 first:pt-0 flex justify-between items-center">
                <div>
                  <p className="font-semibold text-slate-900">{saleLine.description}</p>
                  <p className="text-[11px] text-slate-500 font-mono">
                    Account [{saleLine.account_code}] • {saleLine.quantity} head
                  </p>
                </div>
                <span className="font-mono font-bold text-slate-900">
                  {formatCurrency(saleLine.total_amount)}
                </span>
              </div>
            ))}
            {calculationResult.settlement_lines.filter((l) => l.line_type === 'sales_revenue').length === 0 && (
              <p className="text-xs text-slate-400 italic py-2">No pig sales recorded for this cycle.</p>
            )}

            <div className="pt-3 border-t-2 border-slate-200 flex justify-between items-center font-bold text-slate-900 text-xs">
              <span>TOTAL GROSS CREDITS</span>
              <span className="font-mono text-sm text-emerald-700">
                {formatCurrency(calculationResult.total_sales_revenue)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Right Column: Debits (Total Production Costs) */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="p-4 bg-slate-50/80 border-b border-slate-200">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm font-bold text-slate-900 uppercase tracking-wider">
                2. Total Production Costs (Debits)
              </CardTitle>
              <span className="text-xs font-mono font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded border border-rose-200">
                – {formatCurrency(calculationResult.total_production_expenses)}
              </span>
            </div>
          </CardHeader>
          <CardContent className="p-4 divide-y divide-slate-100 text-xs space-y-3">
            {/* Piglet Cost */}
            <div className="pt-2 first:pt-0 flex justify-between items-center">
              <div>
                <p className="font-semibold text-slate-900">Piglet Placement Cost</p>
                <p className="text-[11px] text-slate-500 font-mono">Account [3010] • {calculationResult.kpis.total_placed_heads} head placed</p>
              </div>
              <span className="font-mono font-bold text-slate-900">
                {formatCurrency(calculationResult.total_piglet_cost)}
              </span>
            </div>

            {/* Feed Cost */}
            <div className="pt-2 flex justify-between items-center">
              <div>
                <p className="font-semibold text-slate-900">Total Feed Consumed</p>
                <p className="text-[11px] text-slate-500 font-mono">
                  Account [3020] • {calculationResult.snapshot_data?.physical_summary?.total_feed_bags ?? '—'} bags
                </p>
              </div>
              <span className="font-mono font-bold text-slate-900">
                {formatCurrency(calculationResult.total_feed_cost)}
              </span>
            </div>

            {/* Med Cost */}
            <div className="pt-2 flex justify-between items-center">
              <div>
                <p className="font-semibold text-slate-900">Veterinary Medicines & Vaccines</p>
                <p className="text-[11px] text-slate-500 font-mono">Account [3030 / 3034]</p>
              </div>
              <span className="font-mono font-bold text-slate-900">
                {formatCurrency(calculationResult.total_med_cost)}
              </span>
            </div>

            {/* Expenses */}
            <div className="pt-2 flex justify-between items-center">
              <div>
                <p className="font-semibold text-slate-900">Hauling, Trucking & Permits</p>
                <p className="text-[11px] text-slate-500 font-mono">Accounts [3050 / 3051]</p>
              </div>
              <span className="font-mono font-bold text-slate-900">
                {formatCurrency(calculationResult.total_other_costs)}
              </span>
            </div>

            <div className="pt-3 border-t-2 border-slate-200 flex justify-between items-center font-bold text-slate-900 text-xs">
              <span>TOTAL PRODUCTION DEBITS</span>
              <span className="font-mono text-sm text-rose-700">
                {formatCurrency(calculationResult.total_production_expenses)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* 3. Net Cycle Profit & Contract Profit Split */}
      <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-white to-slate-50">
        <CardHeader className="p-4 border-b border-slate-200">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <CardTitle className="text-sm font-bold text-slate-900 uppercase tracking-wider">
              3. Net Cycle Profit & Contract Profit Split
            </CardTitle>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Net Operating Profit:</span>
              <span className="text-base font-mono font-extrabold text-indigo-950">
                {formatCurrency(calculationResult.gross_profit)}
              </span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4 space-y-4 text-xs">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Bound Contract
              </label>
              {!isFrozen && workspaceData.availableContracts.length > 0 ? (
                <select
                  value={selectedContractId}
                  onChange={(e) => handleContractChange(e.target.value)}
                  className="w-full h-10 min-h-[44px] px-3 rounded-md border border-slate-300 bg-white text-xs font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                >
                  {workspaceData.availableContracts.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.contract_number} (Grower {c.grower_share_pct}% / VSB {c.vsb_share_pct}%)
                    </option>
                  ))}
                </select>
              ) : (
                <div className="font-bold text-slate-900 py-1 font-mono">
                  {calculationResult.profit_split.contract_code || 'Historical Record / Standard Split'}
                </div>
              )}
            </div>

            <div className="p-3 bg-white rounded border border-slate-200">
              <span className="text-slate-500 font-medium block mb-0.5">
                • Grower Base Share ({calculationResult.profit_split.grower_share_pct}%):
              </span>
              <span className="font-mono text-base font-extrabold text-slate-900">
                {formatCurrency(calculationResult.grower_gross_share)}
              </span>
            </div>

            <div className="p-3 bg-white rounded border border-slate-200">
              <span className="text-slate-500 font-medium block mb-0.5">
                • VSB Farm Base Share ({calculationResult.profit_split.vsb_share_pct}%):
              </span>
              <span className="font-mono text-base font-extrabold text-slate-900">
                {formatCurrency(calculationResult.vsb_share_amount)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 4. Grower Adjustments (Accounts 4010 – 4050) */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <SettlementAdjustmentsGrid
            adjustments={allAdjustments}
            calculationLines={calculationResult.settlement_lines}
            isFrozen={isFrozen}
            onAddClick={() => {
              setAdjustmentToEdit(null);
              setShowAdjustmentModal(true);
            }}
            onEditClick={(adj) => {
              setAdjustmentToEdit(adj);
              setShowAdjustmentModal(true);
            }}
            onVoidClick={(adj) => setAdjustmentToVoid(adj)}
          />
        </CardContent>
      </Card>

      {/* 5. Final Net Payout Callout */}
      <div className="p-6 bg-slate-950 text-white rounded-xl shadow-lg border border-slate-800 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <span className="text-xs uppercase font-extrabold tracking-wider text-amber-400">
              FINAL NET PAYOUT TO GROWER
            </span>
            <p className="text-xs text-slate-300 mt-0.5">
              (Base Share {formatCurrency(calculationResult.grower_gross_share)} {calculationResult.total_adjustments_additions > 0 ? `+ Reimbursements ${formatCurrency(calculationResult.total_adjustments_additions)}` : ''} – Deductions {formatCurrency(calculationResult.total_adjustments_deductions)})
            </p>
          </div>
          <div className="text-right">
            <span className="text-2xl sm:text-3xl font-mono font-black text-amber-400">
              {formatCurrency(calculationResult.grower_net_payout)}
            </span>
          </div>
        </div>
      </div>

      {/* 6. Physical Production KPIs Card */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader className="p-4 bg-slate-50 border-b border-slate-200">
          <CardTitle className="text-xs font-bold text-slate-700 uppercase tracking-wider">
            Physical Performance Metrics (Strict Validation)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
          <div>
            <span className="text-slate-500 block mb-0.5">Pigs Placed:</span>
            <span className="font-mono font-bold text-slate-900">{calculationResult.kpis.total_placed_heads} head</span>
          </div>
          <div>
            <span className="text-slate-500 block mb-0.5">Pigs Sold:</span>
            <span className="font-mono font-bold text-slate-900">{calculationResult.kpis.total_sold_heads} head</span>
          </div>
          <div>
            <span className="text-slate-500 block mb-0.5">Mortality Rate:</span>
            <span className="font-mono font-bold text-slate-900">
              {calculationResult.kpis.mortality_rate_pct !== null ? `${calculationResult.kpis.mortality_rate_pct}%` : 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-slate-500 block mb-0.5">Feed Conversion (FCR):</span>
            <span className="font-mono font-bold text-slate-900">
              {calculationResult.kpis.fcr !== null ? `${calculationResult.kpis.fcr.toFixed(2)} FCR` : 'N/A'}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Action Modals */}
      {showAdjustmentModal && (
        <SettlementAdjustmentModal
          isOpen={true}
          onClose={() => {
            setShowAdjustmentModal(false);
            setAdjustmentToEdit(null);
          }}
          onSuccess={() => loadData(customSplit)}
          cycleId={cycle.id}
          settlementId={existingSettlement?.id || ''}
          farmId={cycle.farm_id}
          growerId={cycle.grower_id}
          adjustmentToEdit={adjustmentToEdit}
        />
      )}

      {showApprovalModal && (
        <SettlementApprovalModal
          isOpen={true}
          onClose={() => setShowApprovalModal(false)}
          onSuccess={() => loadData(customSplit)}
          settlementId={existingSettlement?.id || ''}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          growerName={growerName}
          calculationResult={calculationResult}
          criticalIssues={criticalIssues}
        />
      )}

      {showFreezeModal && (
        <SettlementFreezeModal
          isOpen={true}
          onClose={() => setShowFreezeModal(false)}
          onSuccess={() => loadData(customSplit)}
          settlementId={existingSettlement?.id || ''}
          cycleId={cycle.id}
          cycleCode={cycle.cycle_code}
          growerName={growerName}
          calculationResult={calculationResult}
          criticalIssues={criticalIssues}
        />
      )}

      {showPrintModal && (
        <SettlementStatementPrintModal
          isOpen={true}
          onClose={() => setShowPrintModal(false)}
          cycle={cycle}
          settlement={existingSettlement}
          calculationResult={calculationResult}
        />
      )}

      {adjustmentToVoid && (
        <SoftVoidConfirmModal
          isOpen={true}
          onClose={() => setAdjustmentToVoid(null)}
          onConfirm={handleVoidAdjustment}
          title="Void Settlement Adjustment"
          recordIdentifier={`Adjustment [${adjustmentToVoid.account_code}] - ${adjustmentToVoid.description} (${formatCurrency(adjustmentToVoid.amount)})`}
        />
      )}
    </div>
  );
};
